import { useEffect, useMemo, useState } from "react";
import api, { formatErr } from "../lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { SearchableSelect } from "./SearchableSelect";
import { GitMerge, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export const MergeHotelsDialog = ({ open, onOpenChange, onDone }) => {
  const [loading, setLoading] = useState(false);
  const [dupes, setDupes] = useState([]);
  const [chplHotels, setChplHotels] = useState([]);
  const [targets, setTargets] = useState({});
  const [busy, setBusy] = useState(null);
  const [allHotels, setAllHotels] = useState([]);
  const [pickSource, setPickSource] = useState("");
  const [pickTarget, setPickTarget] = useState("");
  const [pickBusy, setPickBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/hotels/duplicates");
      setDupes(data.duplicates);
      setChplHotels(data.chpl_hotels);
      const t = {};
      data.duplicates.forEach((d) => { if (d.suggested) t[d.source.id] = d.suggested.id; });
      setTargets(t);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      load();
      api.get("/hotels").then((r) => setAllHotels(r.data.filter((h) => !h.archived)));
      setPickSource(""); setPickTarget("");
    }
  }, [open]);

  const hotelSub = (h) => [h.city, h.region, !h.chpl_id && "manual"].filter(Boolean).join(" · ");
  const sourceOptions = useMemo(() => allHotels.filter((h) => h.id !== pickTarget).map((h) => ({ value: h.id, label: h.name, sub: hotelSub(h) })), [allHotels, pickTarget]);
  const targetOptions = useMemo(() => allHotels.filter((h) => h.id !== pickSource).map((h) => ({ value: h.id, label: h.name, sub: hotelSub(h) })), [allHotels, pickSource]);

  const mergePicked = async () => {
    const src = allHotels.find((h) => h.id === pickSource);
    const tgt = allHotels.find((h) => h.id === pickTarget);
    if (!window.confirm(`Merge "${src?.name}" INTO "${tgt?.name}"?\n\nAll audits, actions, schedules and users move to "${tgt?.name}" and "${src?.name}" is archived. This cannot be undone.`)) return;
    setPickBusy(true);
    try {
      const { data } = await api.post("/hotels/merge", { source_id: pickSource, target_id: pickTarget });
      const m = data.moved;
      toast.success(`Merged into ${data.target} — ${m.audits} audits, ${m.actions} actions, ${m.users} users moved`);
      setPickSource(""); setPickTarget("");
      setAllHotels((prev) => prev.filter((h) => h.id !== pickSource));
      setDupes((prev) => prev.filter((x) => x.source.id !== pickSource));
      onDone?.();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setPickBusy(false);
    }
  };

  const merge = async (d) => {
    const targetId = targets[d.source.id];
    if (!targetId) return;
    setBusy(d.source.id);
    try {
      const { data } = await api.post("/hotels/merge", { source_id: d.source.id, target_id: targetId });
      const m = data.moved;
      toast.success(`Merged into ${data.target} — ${m.audits} audits, ${m.actions} actions, ${m.users} users moved`);
      setDupes((prev) => prev.filter((x) => x.source.id !== d.source.id));
      onDone?.();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(null);
    }
  };

  const linkedLabel = (l) =>
    [l.audits && `${l.audits} audits`, l.actions && `${l.actions} actions`, l.schedules && `${l.schedules} schedules`, l.users && `${l.users} users`]
      .filter(Boolean).join(", ") || "no linked data";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="merge-hotels-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><GitMerge className="w-5 h-5 text-[#14532D]" />Merge duplicate hotels</DialogTitle>
          <DialogDescription>
            Manually-added hotels are matched against CHPL-synced properties. Merging moves all audits, actions, schedules and users to the CHPL hotel, then archives the old record.
          </DialogDescription>
        </DialogHeader>

        <div className="border border-[#E7E5DF] rounded-lg p-4 space-y-3 bg-stone-50/50" data-testid="manual-merge-section">
          <p className="text-sm font-semibold text-stone-800">Merge any two hotels</p>
          <p className="text-xs text-stone-500">Pick the duplicate to remove and the hotel to keep — everything moves to the kept hotel.</p>
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] items-center gap-2">
            <SearchableSelect testId="pick-source-hotel" placeholder="Hotel to merge away..."
              options={sourceOptions}
              value={pickSource} onChange={setPickSource} className="bg-white" />
            <ArrowRight className="w-4 h-4 text-stone-400 mx-auto hidden sm:block" />
            <SearchableSelect testId="pick-target-hotel" placeholder="Hotel to keep..."
              options={targetOptions}
              value={pickTarget} onChange={setPickTarget} className="bg-white" />
          </div>
          <Button data-testid="pick-merge-btn" size="sm" className="bg-[#14532D] hover:bg-[#166534]"
            disabled={!pickSource || !pickTarget || pickBusy} onClick={mergePicked}>
            {pickBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><GitMerge className="w-4 h-4 mr-1.5" />Merge these two</>}
          </Button>
        </div>

        <p className="text-xs font-semibold uppercase tracking-wider text-stone-400 mt-1">Suggested duplicates</p>
        {loading ? (
          <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-stone-400" /></div>
        ) : dupes.length === 0 ? (
          <p className="py-8 text-center text-sm text-stone-500" data-testid="no-duplicates-message">No manually-added hotels left — everything is linked to CHPL.</p>
        ) : (
          <div className="space-y-3">
            {dupes.map((d) => (
              <div key={d.source.id} className="border border-[#E7E5DF] rounded-lg p-4 space-y-3" data-testid={`dupe-row-${d.source.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="text-sm font-semibold text-stone-800">{d.source.name}</p>
                    <p className="text-xs text-stone-500">{linkedLabel(d.linked)}{d.suggested && <span className="ml-2 text-emerald-700 font-medium">{d.similarity}% match</span>}</p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs text-stone-500 shrink-0">Merge into:</span>
                  <select
                    data-testid={`merge-target-${d.source.id}`}
                    className="flex-1 min-w-[220px] text-sm border border-stone-200 rounded-md px-2 py-1.5 bg-white"
                    value={targets[d.source.id] || ""}
                    onChange={(e) => setTargets({ ...targets, [d.source.id]: e.target.value })}
                  >
                    <option value="">— select CHPL hotel —</option>
                    {chplHotels.map((h) => (
                      <option key={h.id} value={h.id}>{h.name}{h.region ? ` (${h.region})` : ""}</option>
                    ))}
                  </select>
                  <Button data-testid={`merge-btn-${d.source.id}`} size="sm" className="bg-[#14532D] hover:bg-[#166534]"
                    disabled={!targets[d.source.id] || busy === d.source.id} onClick={() => merge(d)}>
                    {busy === d.source.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><GitMerge className="w-4 h-4 mr-1.5" />Merge</>}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
